import type { WebinarRoomSubtitleCueDto } from '@/api/webinarRoom'
import { liveViewerPositionSeconds } from '../player/positions'
import type { Player } from '../player/usePlayer'
import type { RoomState } from '../types'
import { useNow } from '../useNow'
import { subtitleAt } from './subtitles'
import type { SubtitleStyle } from './subtitles'

/** How often the live line is looked up again: a line lasts a few seconds, so a quarter of one is never noticed. */
export const LIVE_SUBTITLE_TICK_MS = 250

/**
 * The line on screen, over the picture just above the client's title band. Text only, never markup, and hidden from
 * screen readers, which have the sound itself.
 */
export function SubtitleLines({ text, style }: { text: string | null; style: SubtitleStyle }) {
    if (!text) return null
    return (
        <div class="v-subs" aria-hidden="true">
            <div class="v-subs-row"><span class={`v-subs-line is-${style}`}>{text}</span></div>
        </div>
    )
}

interface SubtitlesProps {
    player: Player
    cues: readonly WebinarRoomSubtitleCueDto[]
    style: SubtitleStyle
}

/** A video on demand (the replay, the rehearsal, the Room tab's preview): timed by the player's own position. */
export function OnDemandSubtitles({ player, cues, style }: SubtitlesProps) {
    const ready = player.media.value.status === 'ready'
    return <SubtitleLines text={ready ? subtitleAt(cues, player.time.value) : null} style={style} />
}

/**
 * The live stream: timed by where this viewer is in the recording (`liveViewerPositionSeconds`), the clock the
 * heartbeat and timed polls run on, because a live player's own time is the stream's, not the recording's. Nothing
 * shows while the picture is not moving: autoplay refused, or buffering.
 */
export function LiveSubtitles({ player, cues, style, room }: SubtitlesProps & {
    room: Pick<RoomState, 'VideoState' | 'PositionSeconds' | 'PositionReportedUtc' | 'RecordingStartedUtc' | 'RecordingDurationSeconds'>
}) {
    const now = useNow(LIVE_SUBTITLE_TICK_MS)
    const media = player.media.value
    const playing = media.status === 'ready' && !media.paused && !media.buffering
    const seconds = playing ? liveViewerPositionSeconds(room, now) : null
    return <SubtitleLines text={seconds === null ? null : subtitleAt(cues, seconds)} style={style} />
}
